import * as React from 'react';
import moment from 'moment-timezone';
import { ISearchBox, MessageBar, MessageBarType, SearchBox, Spinner, SpinnerSize } from '@fluentui/react';

import { PageSidePanel } from '../core/PageSidePanel';
import { AsyncVisitSidePanel } from '../core/AsyncVisitSidePanel';
import { PageHeader } from '../core/PageHeader';
import { InfoMessage } from '../core/InfoMessage';
import { visitSearch, visitSearchStatus } from '../../api/elasticsearch';
import { EsVisit } from '../../api/elasticsearch.types';
import { VisitList } from '../../components/core/VisitList';
import { createRef, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { onPageDidMount, onPageWillUnmount } from '../../redux/core';
import { setVisitCode } from '../../redux/ui/actions';
import { RootState } from '../../redux/types';
import { useDebounce } from '../../util/hooks';

export function SearchVisitsPage() {
  const [visits, setVisits] = useState<EsVisit[]>([]);
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [searchFreshness, setSearchFreshness] = useState<Date>();

  const {
    ui: { visitCode },
    visits: { visitSideCardClaim },
  } = useSelector((state: RootState) => state);

  const debounceSearchText = useDebounce(searchText, 500);
  const dispatch = useDispatch();

  const searchControl = createRef<ISearchBox>();

  const search = useCallback(() => {
    visitSearchStatus().then((data) => {
      setSearchFreshness(new Date(data.most_recent_update));
    });

    setVisits([]);
    setIsSearching(true);

    visitSearch(searchText).then(
      (data) => {
        setVisits(data);
        setIsSearching(false);
        setSearchError(false);
      },
      () => {
        setVisits([]);
        setIsSearching(false);
        setSearchError(true);
      }
    );
  }, [searchText]);

  // Focuses on search input field on page load
  useEffect(() => {
    dispatch(onPageDidMount('Search'));
    searchControl.current?.focus();
    return () => {
      dispatch(onPageWillUnmount('Search'));
    };

    // Intentional to not have searchControl in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Updates the search results when input field text is entered
  useEffect(() => {
    if (debounceSearchText.length) {
      dispatch(setVisitCode(''));
      search();
    }
    // Intentional to not have search in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchText, dispatch]);

  const renderStatus = () => {
    if (isSearching) {
      return <Spinner style={{ marginTop: 20 }} size={SpinnerSize.large} label="Searching for you..." />;
    }

    if (searchError) {
      return (
        <MessageBar messageBarType={MessageBarType.error} isMultiline>
          Oh no! An error occurred while searching. Please try again in a minute. If this problem persists, reach out to engineering for support.
        </MessageBar>
      );
    } else if (searchText && !visits.length) {
      return (
        <MessageBar messageBarType={MessageBarType.info} isMultiline>
          There are no results, please refine your search.
        </MessageBar>
      );
    } else if (!visits.length) {
      return (
        <InfoMessage>
          <div>You can search by many things:</div>
          <div>User account name, patient name, patient MRN, visit code, visit status, service name, chart id, and date of birth (MM/DD/YYYY)</div>
        </InfoMessage>
      );
    }
    return null;
  };

  const renderVisitSearchFreshness = () => {
    if (!searchFreshness) {
      return null;
    }
    return (
      <div style={{ position: 'absolute', right: 20, top: 20, fontWeight: 100, fontSize: '21px' }}>Results as of {moment.utc(searchFreshness).format('MM/DD/YYYY - h:mm a')}</div>
    );
  };

  const renderSidePanel = () => {
    if (!visitCode) {
      return null;
    }

    return (
      <PageSidePanel>
        <AsyncVisitSidePanel visitSideCardClaim={visitSideCardClaim} visitCode={visitCode} />
      </PageSidePanel>
    );
  };

  const onSearchChange = (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
    setSearchText(newValue ?? '');
  };

  const COLUMN_KEYS = ['insurance', 'status', 'code', 'service', 'patient', 'address', 'doctor', 'assistant', 'date'];

  return (
    <div>
      <PageHeader title="Visit Search" />
      <div className="flex justify-stretch">
        <SearchBox
          componentRef={searchControl}
          className="flex-grow"
          value={searchText}
          onChange={onSearchChange}
          placeholder="Search all visits from beginning of time"
          data-tid="inp_search"
        />
        {visits.length > 0 && <span style={{ marginLeft: 10, fontSize: 20 }}>Results: {visits.length}</span>}
      </div>

      <VisitList
        selectedVisitCode={visitCode}
        visits={visits as any}
        columns={COLUMN_KEYS}
        isFetching={false}
        markets={{ isFetching: false }}
        onSelected={(code) => dispatch(setVisitCode(code))}
      />
      {renderStatus()}
      {renderVisitSearchFreshness()}
      {renderSidePanel()}
    </div>
  );
}
